import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  Link as MuiLink,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";

import {
  EmptyState,
  ScopeBadge,
} from "./primitives";
import {
  addGroupMember,
  createRoleAssignment,
  getAccounts,
  getApps,
  getGroups,
  getUsers,
} from "../../pages/Directory/api";
import { getProjects } from "../../pages/Access/api";

/**
 * AssignmentPickerDialog — shared searchable picker used by the
 * Directory Groups + Roles + Users detail surfaces.
 *
 * Four modes (see below); routes each submit through the correct
 * coord path per scope. Copy contract: the words shown to operators
 * are "Users" and "Groups". "Principal" is never used (operator lock).
 *
 * SOC 2 logging rule: submit errors surface only status + generic
 * messaging. We never echo other tenants' names/ids into the caller's
 * toast — the caller only sees the ids it clicked.
 *
 *  Mode A — "assign_role"  (two-step: pick who → pick scope)
 *    Props: { mode, roleId, roleName?, scopeAccountId?, onSaved }
 *    Step 1: Users / Groups tabs — multi-select subjects to receive
 *            roleId.
 *    Step 2: Scope selector — PLATFORM / ACCOUNT / PROJECT / APP with
 *            conditional resource pickers. Default scope is inferred
 *            from roleName (SYSTEM_* → PLATFORM, ACCOUNT_* → ACCOUNT,
 *            PROJECT_* → PROJECT); scopeAccountId, when passed, seeds
 *            the account selector.
 *
 *  Mode B — "add_to_group"
 *    Props: { mode, userId, scopeAccountId?, onSaved }
 *    Single tab (Groups) — pick groups to add userId to.
 *
 *  Mode C — "assign_role_to_group"
 *    Props: { mode, groupId, scopeAccountId?, roles, onSaved }
 *    Single list of roles — pick roles to give to groupId.
 *
 *  Mode D — "add_users_to_group"
 *    Props: { mode, groupId, scopeAccountId?, onSaved }
 *    Single tab (Users) — pick users to add to groupId. This is the
 *    inverse of Mode B: same POST /groups/{gid}/members/{uid} route,
 *    driven from the Groups tab rather than a User detail page.
 */
export default function AssignmentPickerDialog({
  open,
  mode,
  onClose,
  onSaved,
  // Role modes:
  roleId,
  roleName,
  // User mode:
  userId,
  // Group mode:
  groupId,
  // Common:
  scopeAccountId = null,
  // Pre-fetched role list for assign_role_to_group so we don't refetch
  // when the parent already loaded the whole /api/roles surface.
  roles: prefetchedRoles,
  // Filter which subject tabs render (defaults to both when applicable).
  subjectTabs = ["users", "groups"],
}) {
  const isRoleToUsersOrGroups = mode === "assign_role";
  const isUserToGroups = mode === "add_to_group";
  const isGroupToRole = mode === "assign_role_to_group";
  const isGroupToUsers = mode === "add_users_to_group";

  // -------- data ----------
  const [users, setUsers] = useState(null);
  const [groups, setGroups] = useState(null);
  const [rolesLocal, setRolesLocal] = useState(prefetchedRoles || null);
  const [loadErr, setLoadErr] = useState(null);

  // -------- selection ----------
  const [tab, setTab] = useState(0);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState(null);

  // -------- assign_role two-step + scope state ----------
  // step: "subjects" | "scope" (only assign_role uses "scope").
  const [step, setStep] = useState("subjects");
  // scope: "PLATFORM" | "ACCOUNT" | "PROJECT" | "APP"
  const [scope, setScope] = useState("PLATFORM");
  const [scopeAccount, setScopeAccount] = useState(scopeAccountId || null);
  const [scopeProject, setScopeProject] = useState(null);
  const [scopeApp, setScopeApp] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [projects, setProjects] = useState(null);
  const [apps, setApps] = useState(null);

  // Reset UI state each time the dialog is (re)opened.
  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setQ("");
    setTab(0);
    setSubmitErr(null);
    setStep("subjects");
    // Best-guess default scope from the role name so the operator
    // usually just accepts and clicks Assign. Falls back to ACCOUNT
    // when the caller already told us which account we're inside.
    const name = String(roleName || "").toUpperCase();
    let def;
    if (name.startsWith("SYSTEM")) def = "PLATFORM";
    else if (name.startsWith("ACCOUNT")) def = "ACCOUNT";
    else if (name.startsWith("PROJECT")) def = "PROJECT";
    else def = scopeAccountId ? "ACCOUNT" : "PLATFORM";
    setScope(def);
    setScopeAccount(scopeAccountId || null);
    setScopeProject(null);
    setScopeApp(null);
  }, [open, roleName, scopeAccountId]);

  // Load data appropriate for the mode when the dialog opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadErr(null);

    const loads = [];
    // Users needed for role→users/groups mode (Users tab) AND for the
    // inverse group→users flow (Add-members-to-group picker).
    if (
      ((isRoleToUsersOrGroups && subjectTabs.includes("users")) ||
        isGroupToUsers) &&
      !users
    ) {
      loads.push(
        getUsers()
          .then((data) => {
            if (!cancelled) setUsers(data);
          })
          .catch((e) => {
            if (!cancelled) setLoadErr(`Failed to load users: ${e.message}`);
          })
      );
    }
    // Groups needed for role→users/groups AND user→groups AND group→role modes.
    if (
      (isRoleToUsersOrGroups && subjectTabs.includes("groups")) ||
      isUserToGroups
    ) {
      if (!groups) {
        loads.push(
          getGroups()
            .then((data) => {
              if (!cancelled) setGroups(data);
            })
            .catch((e) => {
              if (!cancelled) setLoadErr(`Failed to load groups: ${e.message}`);
            })
        );
      }
    }
    // Roles needed for group→role mode when parent didn't pre-fetch.
    if (isGroupToRole && !rolesLocal) {
      // Lazy import to avoid a cycle when the picker is used from a
      // module that also imports the api layer above.
      import("../../pages/Directory/api").then(({ getRoles }) => {
        getRoles()
          .then((data) => {
            if (!cancelled) setRolesLocal(data);
          })
          .catch((e) => {
            if (!cancelled) setLoadErr(`Failed to load roles: ${e.message}`);
          });
      });
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);

  // Lazy-load scope resources (accounts / projects / apps) when the
  // scope step is visible and needs them. Kept out of the initial-load
  // effect so add_to_group / assign_role_to_group don't pay the cost.
  useEffect(() => {
    if (!open || !isRoleToUsersOrGroups || step !== "scope") return;
    let cancelled = false;
    if ((scope === "ACCOUNT" || scope === "PROJECT" || scope === "APP") && !accounts) {
      getAccounts()
        .then((data) => {
          if (!cancelled) setAccounts(data);
        })
        .catch((e) => {
          if (!cancelled) setLoadErr(`Failed to load accounts: ${e.message}`);
        });
    }
    if ((scope === "PROJECT" || scope === "APP") && !projects) {
      getProjects()
        .then((data) => {
          if (!cancelled) setProjects(data);
        })
        .catch((e) => {
          if (!cancelled) setLoadErr(`Failed to load projects: ${e.message}`);
        });
    }
    if (scope === "APP" && !apps) {
      getApps()
        .then((data) => {
          if (!cancelled) setApps(data);
        })
        .catch((e) => {
          if (!cancelled) setLoadErr(`Failed to load apps: ${e.message}`);
        });
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step, scope, isRoleToUsersOrGroups]);

  // -------- filtering ----------
  const activeList = useMemo(() => {
    if (isRoleToUsersOrGroups) {
      if (tab === 0 && subjectTabs.includes("users")) {
        return { kind: "user", items: users || [] };
      }
      return { kind: "group", items: groups || [] };
    }
    if (isUserToGroups) {
      return { kind: "group", items: groups || [] };
    }
    if (isGroupToUsers) {
      return { kind: "user", items: users || [] };
    }
    // group→role
    return { kind: "role", items: rolesLocal || [] };
  }, [
    isRoleToUsersOrGroups,
    isUserToGroups,
    isGroupToUsers,
    tab,
    users,
    groups,
    rolesLocal,
    subjectTabs,
  ]);

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return activeList.items;
    return activeList.items.filter((row) => {
      const bag = [
        row.name,
        row.email,
        row.id,
        row.accountName,
        row.description,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return bag.includes(n);
    });
  }, [activeList, q]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // -------- submit ----------
  const submit = async () => {
    if (!selected.size) return;
    setSubmitting(true);
    setSubmitErr(null);

    const ids = Array.from(selected);
    const kind = activeList.kind; // "user" | "group" | "role"
    const errors = [];

    // Resolve scope inputs for assign_role. PLATFORM has no
    // account/resource; ACCOUNT needs an account; PROJECT + APP need
    // an account plus resourceType/resourceId.
    const isPlatform = scope === "PLATFORM";
    const isApp = scope === "APP";
    const isProject = scope === "PROJECT";
    const effectiveAccountId = isPlatform ? null : scopeAccount;
    const resourceType = isApp ? "APP" : isProject ? "PROJECT" : null;
    const resourceId = isApp ? scopeApp : isProject ? scopeProject : null;

    try {
      for (const id of ids) {
        try {
          if (isRoleToUsersOrGroups) {
            if (effectiveAccountId) {
              await createRoleAssignment({
                accountId: effectiveAccountId,
                subjectType: kind === "user" ? "USER" : "GROUP",
                subjectId: id,
                roleId,
                resourceType,
                resourceId,
              });
            } else if (kind === "group") {
              await createRoleAssignment({ groupId: id, roleId });
            } else {
              errors.push(
                "Platform-scope role→user assignment requires an account scope."
              );
            }
          } else if (isUserToGroups) {
            await addGroupMember({
              scopeAccountId,
              gid: id,
              userId,
            });
          } else if (isGroupToUsers) {
            await addGroupMember({
              scopeAccountId,
              gid: groupId,
              userId: id,
            });
          } else if (isGroupToRole) {
            if (scopeAccountId) {
              await createRoleAssignment({
                accountId: scopeAccountId,
                subjectType: "GROUP",
                subjectId: groupId,
                roleId: id,
              });
            } else {
              await createRoleAssignment({ groupId, roleId: id });
            }
          }
        } catch (e) {
          // Preserve status + the caller's own id for de-dup logging.
          // Never leak coord's raw body into the toast per SOC 2 rule.
          errors.push(
            `HTTP ${e.status || "?"} on ${kind} ${id.slice(0, 8)}…`
          );
        }
      }
      if (errors.length) {
        setSubmitErr(
          `${ids.length - errors.length}/${ids.length} succeeded. ${errors.join("; ")}`
        );
      }
      if (errors.length < ids.length) {
        onSaved && onSaved({ count: ids.length - errors.length });
        if (!errors.length) onClose && onClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const title = isRoleToUsersOrGroups
    ? "Assign role to…"
    : isUserToGroups
      ? "Add to group…"
      : isGroupToUsers
        ? "Add members to group…"
        : "Give this group a role…";

  const primaryLabel = isRoleToUsersOrGroups
    ? "Assign"
    : isUserToGroups || isGroupToUsers
      ? "Add"
      : "Assign";

  const showTabs = isRoleToUsersOrGroups && subjectTabs.length > 1;
  const activeTabs = isRoleToUsersOrGroups
    ? subjectTabs.map((k) => (k === "users" ? "Users" : "Groups"))
    : [];

  const rowKey = (row) => row.id;
  const rowLabel = (row) => {
    if (activeList.kind === "user") {
      return {
        primary: row.name || row.email || row.id,
        secondary: row.email || row.id,
      };
    }
    if (activeList.kind === "group") {
      return {
        primary: row.name || row.id,
        secondary: row.accountName || row.id,
      };
    }
    return {
      primary: row.name || row.id,
      secondary: row.description || row.id,
    };
  };

  const isLoading =
    (isRoleToUsersOrGroups &&
      ((subjectTabs.includes("users") && users === null) ||
        groups === null)) ||
    (isUserToGroups && groups === null) ||
    (isGroupToUsers && users === null) ||
    (isGroupToRole && rolesLocal === null);

  const emptyIcon =
    activeList.kind === "user" ? (
      <PeopleOutlinedIcon />
    ) : activeList.kind === "group" ? (
      <WorkspacesOutlinedIcon />
    ) : (
      <ShieldOutlinedIcon />
    );

  const emptyCopy = emptyStateCopy({
    kind: activeList.kind,
    mode,
    filtered: !!q,
  });

  // Scope-step validity: which resource ids the chosen scope requires.
  const scopeValid =
    scope === "PLATFORM"
      ? true
      : scope === "ACCOUNT"
        ? !!scopeAccount
        : scope === "PROJECT"
          ? !!scopeAccount && !!scopeProject
          : scope === "APP"
            ? !!scopeAccount && !!scopeProject && !!scopeApp
            : false;

  const isTwoStep = isRoleToUsersOrGroups;
  const onScopeStep = isTwoStep && step === "scope";

  // Header scope hint: mirrors the current scope selection so the
  // operator can see what they're granting to at all times.
  const headerScopeBadge = onScopeStep && scope === "PROJECT" ? (
    <ScopeBadge scope="project" name={scopeProject || undefined} />
  ) : onScopeStep && scope === "APP" ? (
    <ScopeBadge scope="project" name={scopeApp || undefined} />
  ) : (onScopeStep && scope === "ACCOUNT") || (!onScopeStep && scopeAccountId) ? (
    <ScopeBadge
      scope="account"
      name={scopeAccount || scopeAccountId || undefined}
    />
  ) : (
    <ScopeBadge scope="system" />
  );

  // Project options filtered by the chosen account so the operator
  // never assigns to a project outside the picked account.
  const projectsForScope = useMemo(() => {
    if (!Array.isArray(projects)) return [];
    if (!scopeAccount) return projects;
    return projects.filter(
      (p) => !p.accountId || p.accountId === scopeAccount
    );
  }, [projects, scopeAccount]);

  // App options: coord's kn-service list doesn't uniformly carry
  // {accountId, projectId} today so we render the full list and rely
  // on the caller to know the app's owning project. When those fields
  // are present we filter — otherwise everything shows.
  const appsForScope = useMemo(() => {
    if (!Array.isArray(apps)) return [];
    return apps.filter((a) => {
      if (a.projectId && scopeProject && a.projectId !== scopeProject) return false;
      if (a.accountId && scopeAccount && a.accountId !== scopeAccount) return false;
      return true;
    });
  }, [apps, scopeProject, scopeAccount]);

  return (
    <Dialog
      open={!!open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ timeout: 200 }}
    >
      <DialogTitle>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ flexWrap: "wrap" }}
        >
          <Typography variant="h6" component="span">
            {onScopeStep ? "Choose scope" : title}
          </Typography>
<Tooltip
            title={
              onScopeStep
                ? "Where this assignment applies. Change the scope below to widen or narrow."
                : scopeAccountId
                  ? "Scope — this assignment applies inside the account shown. You'll confirm the exact scope after picking subjects."
                  : "Scope — platform-wide by default (SYSTEM). You'll confirm the exact scope after picking subjects."
            }
            arrow
            enterDelay={400}
          >
            <span>{headerScopeBadge}</span>
          </Tooltip>
        </Stack>
        {isTwoStep && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5 }}
          >
            {onScopeStep
              ? "Where does this role apply? Platform grants everywhere; narrower scopes bind the role to one account, project, or app."
              : "Pick who holds this role — user or group. You'll choose the scope next."}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers sx={{ px: 0 }}>
        {loadErr && (
          <Alert severity="error" sx={{ mx: 3, mb: 2 }}>
            {loadErr}
          </Alert>
        )}

        {onScopeStep && (
          <ScopeSelectorPanel
            scope={scope}
            setScope={setScope}
            accounts={accounts}
            projects={projectsForScope}
            apps={appsForScope}
            scopeAccount={scopeAccount}
            setScopeAccount={(v) => {
              setScopeAccount(v);
              // reset narrower selections when the account changes
              setScopeProject(null);
              setScopeApp(null);
            }}
            scopeProject={scopeProject}
            setScopeProject={(v) => {
              setScopeProject(v);
              setScopeApp(null);
            }}
            scopeApp={scopeApp}
            setScopeApp={setScopeApp}
            selectedCount={selected.size}
          />
        )}

        {!onScopeStep && showTabs && (
          <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => {
                setTab(v);
                setSelected(new Set());
              }}
            >
              {activeTabs.map((label) => (
                <Tab key={label} label={label} />
              ))}
            </Tabs>
          </Box>
        )}

{!onScopeStep && (
          <Box sx={{ px: 3, py: 2 }}>
            <TextField
              size="small"
              fullWidth
              placeholder={
                activeList.kind === "user"
                  ? "Search users by name or email"
                  : activeList.kind === "group"
                    ? "Search groups by name"
                    : "Search roles by name or description"
              }
              value={q}
              onChange={(e) => setQ(e.target.value)}
              inputProps={{
                "aria-label":
                  activeList.kind === "user"
                    ? "Search users"
                    : activeList.kind === "group"
                      ? "Search groups"
                      : "Search roles",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}

        {!onScopeStep && isLoading ? (
          <Box
            sx={{ px: 3, py: 1, maxHeight: 320, overflow: "hidden" }}
            aria-busy="true"
          >
            {Array.from({ length: 5 }).map((_, i) => (
              <Stack
                key={i}
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ py: 1 }}
              >
                <Skeleton variant="rectangular" width={18} height={18} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width={`${60 + (i % 3) * 8}%`} />
                  <Skeleton variant="text" width={`${40 + (i % 3) * 6}%`} />
                </Box>
              </Stack>
            ))}
          </Box>
        ) : !onScopeStep ? (
          <Box>
            {filtered.length === 0 ? (
              <Box sx={{ px: 3, py: 2 }}>
                <EmptyState
                  compact
                  icon={emptyIcon}
                  title={emptyCopy.title}
                  description={emptyCopy.description}
                  variant={q ? "no-match" : "empty"}
                />
              </Box>
            ) : (
              <List dense sx={{ maxHeight: 320, overflow: "auto", py: 0 }}>
                {filtered.map((row) => {
                  const key = rowKey(row);
                  const { primary, secondary } = rowLabel(row);
                  const checked = selected.has(key);
                  return (
                    <ListItemButton
                      key={key}
                      dense
                      onClick={() => toggle(key)}
                      selected={checked}
                    >
                      <Checkbox
                        edge="start"
                        checked={checked}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{ "aria-label": primary }}
                      />
                      <ListItemText
                        primary={primary}
                        secondary={secondary}
                        primaryTypographyProps={{ fontWeight: 500 }}
                        secondaryTypographyProps={{
                          sx: {
                            fontFamily: /^[0-9a-f-]{8,}$/.test(String(secondary))
                              ? "monospace"
                              : undefined,
                            fontSize: 12,
                          },
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Box>
        ) : null}

        {submitErr && (
          <Alert severity="error" sx={{ mx: 3, mt: 2 }}>
            {submitErr}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ mr: "auto" }}
        >
          <Chip
            size="small"
            label={`${selected.size} selected`}
            color={selected.size ? "primary" : "default"}
            variant="outlined"
          />
{selected.size > 0 && !onScopeStep && (
            <Tooltip title="Unselect everything picked so far." arrow enterDelay={400}>
              <MuiLink
                component="button"
                type="button"
                underline="hover"
                onClick={() => setSelected(new Set())}
                sx={{ fontSize: 12 }}
                aria-label="Clear selection"
              >
                Clear
              </MuiLink>
            </Tooltip>
          )}
        </Stack>
        {onScopeStep ? (
          <Button
            onClick={() => setStep("subjects")}
            disabled={submitting}
          >
            Back
          </Button>
        ) : (
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
        )}
        {isTwoStep && !onScopeStep ? (
          <Button
            variant="contained"
            onClick={() => setStep("scope")}
            disabled={!selected.size}
          >
            Next: choose scope
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={submit}
            disabled={submitting || !selected.size || (isTwoStep && !scopeValid)}
          >
            {submitting ? "Saving…" : primaryLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/**
 * ScopeSelectorPanel — assign_role step 2. Radio buttons for the four
 * scopes plus conditional resource pickers underneath. Kept internal
 * to this file so the two-step flow lives in one module.
 *
 * Copy note: no "principal" word. We refer to who holds the role by
 * counting selections ("N selected") so future ServicePrincipals fit.
 */
function ScopeSelectorPanel({
  scope,
  setScope,
  accounts,
  projects,
  apps,
  scopeAccount,
  setScopeAccount,
  scopeProject,
  setScopeProject,
  scopeApp,
  setScopeApp,
  selectedCount,
}) {
  const needsAccount = scope !== "PLATFORM";
  const needsProject = scope === "PROJECT" || scope === "APP";
  const needsApp = scope === "APP";

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <FormControl>
        <RadioGroup
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          aria-label="Assignment scope"
        >
          <FormControlLabel
            value="PLATFORM"
            control={
              <Radio size="small" inputProps={{ "aria-label": "Platform" }} />
            }
            label={
              <ScopeLabel
                title="Platform"
                hint="Grants the role everywhere. SYSTEM operators only."
              />
            }
          />
          <FormControlLabel
            value="ACCOUNT"
            control={
              <Radio size="small" inputProps={{ "aria-label": "Account" }} />
            }
            label={
              <ScopeLabel
                title="Account"
                hint="Grants inside one account — every project and app under it."
              />
            }
          />
          <FormControlLabel
            value="PROJECT"
            control={
              <Radio size="small" inputProps={{ "aria-label": "Project" }} />
            }
            label={
              <ScopeLabel
                title="Project"
                hint="Grants inside one project — every app under it."
              />
            }
          />
          <FormControlLabel
            value="APP"
            control={
              <Radio size="small" inputProps={{ "aria-label": "App" }} />
            }
            label={
              <ScopeLabel
                title="App"
                hint="Grants against a single app. Tightest scope."
              />
            }
          />
        </RadioGroup>
      </FormControl>

      {needsAccount && (
        <Box sx={{ mt: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="scope-account-label">Account</InputLabel>
            <Select
              labelId="scope-account-label"
              label="Account"
              // MUI warns when `value` isn't in the option list. Wait
              // until the accounts fetch resolves before honoring a
              // preseeded scopeAccount so the transient warning is
              // avoided on first paint.
              value={
                Array.isArray(accounts) &&
                accounts.some((a) => a.id === scopeAccount)
                  ? scopeAccount
                  : ""
              }
              onChange={(e) => setScopeAccount(e.target.value)}
              inputProps={{ "aria-label": "Account" }}
            >
              {(accounts || []).length === 0 && (
                <MenuItem value="" disabled>
                  {accounts === null ? "Loading accounts…" : "No accounts visible"}
                </MenuItem>
              )}
              {(accounts || []).map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.name || a.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {needsProject && (
        <Box sx={{ mt: 2 }}>
          <FormControl fullWidth size="small" disabled={!scopeAccount}>
            <InputLabel id="scope-project-label">Project</InputLabel>
            <Select
              labelId="scope-project-label"
              label="Project"
              value={
                Array.isArray(projects) &&
                projects.some((p) => (p.id || p.name) === scopeProject)
                  ? scopeProject
                  : ""
              }
              onChange={(e) => setScopeProject(e.target.value)}
              inputProps={{ "aria-label": "Project" }}
            >
              {(projects || []).length === 0 && (
                <MenuItem value="" disabled>
                  {projects === null
                    ? "Loading projects…"
                    : scopeAccount
                      ? "No projects in this account"
                      : "Pick an account first"}
                </MenuItem>
              )}
              {(projects || []).map((p) => (
                <MenuItem key={p.id || p.name} value={p.id || p.name}>
                  {p.name || p.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {needsApp && (
        <Box sx={{ mt: 2 }}>
          <FormControl fullWidth size="small" disabled={!scopeProject}>
            <InputLabel id="scope-app-label">App</InputLabel>
            <Select
              labelId="scope-app-label"
              label="App"
              value={
                Array.isArray(apps) &&
                apps.some((a) => (a.id || a.name) === scopeApp)
                  ? scopeApp
                  : ""
              }
              onChange={(e) => setScopeApp(e.target.value)}
              inputProps={{ "aria-label": "App" }}
            >
              {(apps || []).length === 0 && (
                <MenuItem value="" disabled>
                  {apps === null
                    ? "Loading apps…"
                    : scopeProject
                      ? "No apps in this project"
                      : "Pick a project first"}
                </MenuItem>
              )}
              {(apps || []).map((a) => (
                <MenuItem key={a.id || a.name} value={a.id || a.name}>
                  {a.displayName || a.name || a.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 2 }}
      >
        Granting to {selectedCount} selected.
      </Typography>
    </Box>
  );
}

function ScopeLabel({ title, hint }) {
  return (
    <Box>
      <Typography variant="body2" fontWeight={500}>
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {hint}
      </Typography>
    </Box>
  );
}

function emptyStateCopy({ kind, mode, filtered }) {
  if (filtered) {
    return {
      title: "No matches",
      description:
        "Try clearing the filter or searching for a different name.",
    };
  }
  if (kind === "user") {
    if (mode === "add_users_to_group") {
      return {
        title: "No users to pick from",
        description:
          "Invite users to the account first, then come back to add them to this group.",
      };
    }
    return {
      title: "No users to pick from",
      description:
        "Users appear here as they redeem invitations across accounts.",
    };
  }
  if (kind === "role") {
    return {
      title: "No roles to pick from",
      description: "Create a role first, then come back to assign it.",
    };
  }
  // group
  if (mode === "add_to_group") {
    return {
      title: "No groups to pick from",
      description:
        "Create a group first — then adding this user is one click away.",
    };
  }
  return {
    title: "No groups to pick from",
    description: "Create a group first, then come back to assign this role.",
  };
}
